import { useState, useEffect } from 'react';
import { toast } from 'sonner';

// 🟢 Ensure this path matches where you saved followerApi.ts
import { followerAPI } from '../../api/expertFollowerApis/followerApi'; 

export function useFollower(expertCode: string | undefined, userCode: string | null) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [isLoadingFollowData, setIsLoadingFollowData] = useState(true);

  // 🟢 Load initial data when profile mounts
  useEffect(() => {
    if (!expertCode) {
      setIsLoadingFollowData(false);
      return;
    }

    const fetchFollowData = async () => {
      setIsLoadingFollowData(true);
      try {
        // 1. Get total count
        const countData = await followerAPI.getFollowerCount(expertCode);
        setFollowerCount(countData.count || 0);

        // 2. If logged in, get specific follow status
        if (userCode) {
          const statusData = await followerAPI.checkFollowStatus(expertCode, userCode);
          setIsFollowing(statusData.isFollowing);
        }
      } catch (error) {
        console.error("Failed to load follower data:", error);
      } finally {
        setIsLoadingFollowData(false);
      }
    };

    fetchFollowData();
  }, [expertCode, userCode]);

  // 🟢 Handle button click with Optimistic UI Update
  const handleToggleFollow = async () => {
    if (!userCode) {
      toast.error("Please log in to follow experts");
      return;
    }
    if (!expertCode) return;

    // ⚡ Optimistic Update: Change UI instantly before server responds for a snappy feel
    const previousIsFollowing = isFollowing;
    setIsFollowing(!previousIsFollowing);
    setFollowerCount(prev => !previousIsFollowing ? prev + 1 : prev - 1);

    try {
      const data = await followerAPI.toggleFollow(expertCode, userCode);
      
      // Sync with absolute truth from backend
      setIsFollowing(data.isFollowing);
      
      // Show appropriate toast message
      if (data.isFollowing) {
        toast.success(data.message || "You are now following this expert!");
      } else {
        toast.success(data.message || "Unfollowed successfully.");
      }
    } catch (error) {
      // ❌ If API fails, revert the optimistic update back to what it was
      setIsFollowing(previousIsFollowing);
      setFollowerCount(prev => previousIsFollowing ? prev + 1 : prev - 1);
      toast.error("Failed to update follow status. Please try again.");
      console.error("Toggle follow error:", error);
    }
  };

  return {
    isFollowing,
    followerCount,
    isLoadingFollowData,
    handleToggleFollow
  };
}